import { useEffect, useRef, useState } from "react";
import { renderBanner, widestLine, isAscii, transliterate, ASCII_FONTS } from "./ascii";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Converts text into ASCII art — an image made entirely of text characters — for a retro-style banner or decorative text in a plain-text context like a code comment, terminal output, or README file, where an actual image file isn't an option.`,
  how: `Type your text (several lines make several banners), pick a font and alignment, and copy the result. The preview can be zoomed; a note tells you if the art is wider than 80 columns.`,
  tip: `ASCII art displays correctly only in a monospaced font — if you paste it somewhere with a proportional font, the alignment will look broken even though the text itself is correct. Code editors, terminals, and plain-text files almost always use a monospaced font by default, which is why this works well for those specific contexts but not for a regular word processor or webpage without adjusting the font first.`,
};

export default function AsciiArtGenerator() {
  const [text, setText] = useState("Hello");
  const [font, setFont] = useState("Standard");
  const [align, setAlign] = useState<"left" | "center">("left");
  const [art, setArt] = useState("");
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(12);

  const taRef = useRef<HTMLTextAreaElement>(null);
  // Text typed before the page finished loading sits in the DOM only; adopt it instead of overwriting it with the default.
  useEffect(() => {
    const v = taRef.current?.value;
    if (v !== undefined && v !== text) setText(v.slice(0, 60));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plain = transliterate(text);
  const changed = plain !== text;

  useEffect(() => {
    let live = true;
    if (!plain.trim()) { setArt(""); setError(""); return; }
    if (!isAscii(plain)) { setArt(""); setError("Banner fonts only contain plain English letters, digits and punctuation. Hindi, emoji and other scripts can't be drawn — remove them to continue. (Accented Latin letters are converted automatically.)"); return; }
    renderBanner(plain, font, align).then((r) => { if (live) { setArt(r); setError(""); } }).catch(() => { if (live) setError("Couldn't load that font. Try another one."); });
    return () => { live = false; };
  }, [plain, font, align]);

  const copy = () => {
    void copyText(art, "Copied to clipboard");
  };

  const { blocked } = useLogToolUsage(art, undefined, { ownActions: true });
  const width = widestLine(art);

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Label className="mb-2 block">Text (one banner per line)</Label>
          <Textarea aria-label="Text (one banner per line)" ref={taRef} value={text} onChange={e => setText(e.target.value.slice(0, 60))} placeholder="Hello" rows={2} />
        </div>
        <div className="space-y-3">
          <div>
            <Label className="mb-2 block">Font</Label>
            <select className="border rounded px-2 py-1.5 w-full bg-background text-sm" value={font} onChange={e => setFont(e.target.value)} aria-label="Banner font">
              {Object.keys(ASCII_FONTS).map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div className="flex gap-2" role="group" aria-label="Alignment">
            {(["left", "center"] as const).map(a => <Button key={a} type="button" size="sm" variant={align === a ? "default" : "outline"} aria-pressed={align === a} onClick={() => setAlign(a)}>{a === "left" ? "Left" : "Centre"}</Button>)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <Label className="text-sm shrink-0">Preview zoom</Label>
        <input type="range" min={8} max={22} value={zoom} onChange={e => setZoom(Number(e.target.value))} className="w-full" aria-label="Preview zoom" />
      </div>
      {changed && isAscii(plain) && <p className="mt-4 text-xs text-muted-foreground" role="status" data-testid="ascii-translit">Accented letters were drawn without their accents (for example "{plain.split("\n")[0].slice(0, 30)}"), because banner fonts only have plain letters.</p>}
      {error && <p className="mt-4 text-sm text-destructive" role="alert">{error}</p>}
      {art && !blocked && (
        <div className="mt-4">
          <pre data-testid="ascii-art" className="rounded-lg border border-border bg-muted/30 p-4 overflow-x-auto font-mono whitespace-pre" style={{ fontSize: zoom, lineHeight: 1.1 }}>
            {art}
          </pre>
          <p className="text-xs text-muted-foreground mt-1">{width} characters wide{width > 80 ? " — wider than 80 columns, so it may wrap in a terminal or README. Try a smaller font or shorter text." : ""}</p>
          <Button className="mt-3" onClick={copy}>
            Copy ASCII Art
          </Button>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
